import React from "react";
import type { BannerState } from "../../../utils/@types";
import { ChannelInfo } from "../../../utils/@types";
import AcceptCurrentAsDefaultButton from "./AcceptCurrentAsDefaultButton";
import LazyBannerImage from "./LazyBannerImage";
import {
  bannerTablePreviewBoxStyle,
  resolveCurrentBannerDisplayUrl,
} from "./bannerImageUtils";
import {
  bannerStateLabel,
  resolveBannerState,
  shouldOfferAcceptCurrentAsDefault,
  type BannerStateRow,
} from "./bannerStateUtils";

export type ChannelBannerRow = ChannelInfo &
  BannerStateRow & {
    CurrentBannerCachedURL?: string;
    DefaultBannerURL?: string;
  };

const BANNER_STATE_STYLES: Record<
  BannerState,
  { className: string; title?: string }
> = {
  default: {
    className: "text-gray-400",
  },
  no_default: {
    className:
      "text-xs font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 inline-block",
    title: "No default banner saved for this channel",
  },
  brand_boost: {
    className:
      "text-xs font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-900 inline-block",
    title: "Active Banner Swap campaign window",
  },
  external_change: {
    className:
      "text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 inline-block",
    title: "Banner changed outside Banner Swap tool",
  },
};

export const BannerTemplateCurrent = (rowData: ChannelBannerRow) => {
  if (!rowData) {
    return (
      <div>
        <span style={{ color: "gray" }}>No banner available</span>
      </div>
    );
  }

  const displayUrl = resolveCurrentBannerDisplayUrl(rowData);

  return (
    <div className="flex flex-col gap-1 items-start banner-table-preview-cell">
      <LazyBannerImage
        src={displayUrl}
        alt="Current channel banner"
      />
    </div>
  );
};

export const BannerTemplateBannerState = (rowData: ChannelBannerRow) => {
  const state = resolveBannerState(rowData ?? {});
  if (state === "default") {
    return <span className="text-gray-400">—</span>;
  }

  const style = BANNER_STATE_STYLES[state];
  const externalAt = rowData?.externalBannerChangeAt?.trim();
  let title = style.title;
  if (state === "external_change" && externalAt) {
    const detectedAt = new Date(externalAt);
    const dateLabel = Number.isNaN(detectedAt.getTime())
      ? externalAt
      : detectedAt.toLocaleString();
    title = `${style.title} (${dateLabel})`;
  }

  return (
    <span className={style.className} title={title}>
      {bannerStateLabel(state)}
    </span>
  );
};

export type BannerTemplateDefaultProps = {
  rowData: ChannelBannerRow & { DefaultBannerURL?: string };
  onAcceptCurrentAsDefault?: (channelId: string) => void;
  accepting?: boolean;
};

export const BannerTemplateDefault = ({
  rowData,
  onAcceptCurrentAsDefault,
  accepting = false,
}: BannerTemplateDefaultProps) => {
  if (!rowData) {
    return (
      <div>
        <span style={{ color: "gray" }}>No banner available</span>
      </div>
    );
  }

  const showAcceptAction = Boolean(
    onAcceptCurrentAsDefault && shouldOfferAcceptCurrentAsDefault(rowData)
  );
  const state = resolveBannerState(rowData);
  const hasDefault = Boolean(rowData.DefaultBannerURL?.trim());
  const currentBannerUrl = resolveCurrentBannerDisplayUrl(rowData);

  return (
    <div className="flex flex-col gap-2 items-start banner-table-preview-cell">
      {hasDefault ? (
        <LazyBannerImage
          src={rowData.DefaultBannerURL!}
          alt="Default channel banner"
        />
      ) : showAcceptAction ? (
        <AcceptCurrentAsDefaultButton
          currentBannerUrl={currentBannerUrl}
          channelId={rowData.ChannelId}
          onAccept={onAcceptCurrentAsDefault!}
          accepting={accepting}
        />
      ) : (
        <div
          className="flex items-center justify-center text-sm text-gray-500 bg-gray-50 border border-dashed border-gray-300 rounded"
          style={bannerTablePreviewBoxStyle}
        >
          No default set
        </div>
      )}

      {showAcceptAction && hasDefault && (
        <div className="flex flex-col gap-1 items-start">
          {state === "external_change" && (
            <span className="text-xs text-amber-800 leading-snug">
              Live banner differs from saved default.
            </span>
          )}
          <AcceptCurrentAsDefaultButton
            currentBannerUrl={currentBannerUrl}
            channelId={rowData.ChannelId}
            onAccept={onAcceptCurrentAsDefault!}
            accepting={accepting}
          />
        </div>
      )}
    </div>
  );
};
